'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import { inboxV2GithubPullRequestSearchQueryKey } from './query-keys';
import { useSelectedRepositories } from '@/lib/hooks/use-selected-repositories';
const REMOTE_SEARCH_DEBOUNCE_MS = 300;
const REMOTE_SEARCH_STALLED_MS = 200;
const REMOTE_SEARCH_STALE_TIME_MS = 60_000;
const REMOTE_SEARCH_GC_TIME_MS = 5 * 60_000;
export type InboxV2GithubPullRequestSearchResult = {
  owner: string;
  repo: string;
  number: number;
  title: string;
  author: string;
  url: string;
  state: string;
  draft: boolean;
  mergedAt: string | null;
  updatedAt: string;
  authorAvatarUrl: string | null;
  source: 'github';
};
const EMPTY_SEARCH_RESULTS: InboxV2GithubPullRequestSearchResult[] = [];
type InboxV2GithubPullRequestSearchResponse = {
  pullRequests: InboxV2GithubPullRequestSearchResult[];
  error?: 'github_rate_limited' | 'github_search_failed';
};
type UseInboxV2GithubPullRequestSearchOptions = {
  query: string;
  enabled?: boolean;
};
function normalizeSearchTerm(value: string) {
  return value.trim().replace(/^[/#]+/, '').trim();
}
function shouldRunRemoteSearch(query: string) {
  return query.length >= 2 || /^#?\d+$/.test(query);
}
export function useInboxV2GithubPullRequestSearch(t0) {
  const $ = _c(30);
  const {
    query,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== query) {
    t2 = normalizeSearchTerm(query);
    $[0] = query;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const normalizedSearch = t2;
  const [debouncedSearch] = useDebounceValue(normalizedSearch, REMOTE_SEARCH_DEBOUNCE_MS);
  const isSearchSettled = normalizedSearch === debouncedSearch;
  let t3;
  if ($[2] !== debouncedSearch) {
    t3 = shouldRunRemoteSearch(debouncedSearch);
    $[2] = debouncedSearch;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const shouldSearch = t3;
  let t4;
  if ($[4] !== enabled) {
    t4 = {
      enabled
    };
    $[4] = enabled;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const {
    selectedRepositoryIds,
    normalizedInstallationId
  } = useSelectedRepositories(t4);
  let t5;
  if ($[6] !== debouncedSearch || $[7] !== normalizedInstallationId || $[8] !== selectedRepositoryIds) {
    t5 = inboxV2GithubPullRequestSearchQueryKey(normalizedInstallationId, selectedRepositoryIds, debouncedSearch);
    $[6] = debouncedSearch;
    $[7] = normalizedInstallationId;
    $[8] = selectedRepositoryIds;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== debouncedSearch || $[11] !== normalizedInstallationId) {
    t6 = async t7 => {
      const {
        signal
      } = t7;
      const params = new URLSearchParams({
        q: debouncedSearch
      });
      if (normalizedInstallationId !== null && normalizedInstallationId !== undefined) {
        params.set("installationId", String(normalizedInstallationId));
      }
      const response = await fetch(`/inbox/api/pull-requests/search?${params.toString()}`, {
        credentials: "include",
        signal
      });
      if (!response.ok) {
        throw new Error("Failed to search pull requests");
      }
      return response.json() as Promise<InboxV2GithubPullRequestSearchResponse>;
    };
    $[10] = debouncedSearch;
    $[11] = normalizedInstallationId;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = enabled && isSearchSettled && shouldSearch && selectedRepositoryIds.length > 0;
  let t8;
  if ($[13] !== t5 || $[14] !== t6 || $[15] !== t7) {
    t8 = {
      queryKey: t5,
      queryFn: t6,
      enabled: t7,
      staleTime: REMOTE_SEARCH_STALE_TIME_MS,
      gcTime: REMOTE_SEARCH_GC_TIME_MS,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false
    };
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const searchQuery = useQuery(t8);
  const [isStalled, setIsStalled] = useState(false);
  let t10;
  let t9;
  if ($[17] !== searchQuery.isFetching) {
    t9 = () => {
      if (!searchQuery.isFetching) {
        setIsStalled(false);
        return;
      }
      const timeoutId = window.setTimeout(() => {
        setIsStalled(true);
      }, REMOTE_SEARCH_STALLED_MS);
      return () => {
        window.clearTimeout(timeoutId);
      };
    };
    t10 = [searchQuery.isFetching];
    $[17] = searchQuery.isFetching;
    $[18] = t10;
    $[19] = t9;
  } else {
    t10 = $[18];
    t9 = $[19];
  }
  useEffect(t9, t10);
  const t11 = searchQuery.data?.pullRequests ?? EMPTY_SEARCH_RESULTS;
  let t12;
  if ($[20] !== isSearchSettled || $[21] !== normalizedSearch) {
    t12 = !isSearchSettled && shouldRunRemoteSearch(normalizedSearch);
    $[20] = isSearchSettled;
    $[21] = normalizedSearch;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== debouncedSearch || $[24] !== isStalled || $[25] !== searchQuery.error || $[26] !== searchQuery.isFetching || $[27] !== t11 || $[28] !== t12) {
    t13 = {
      results: t11,
      isFetching: searchQuery.isFetching,
      isPending: t12,
      isStalled,
      error: searchQuery.error,
      debouncedSearch
    };
    $[23] = debouncedSearch;
    $[24] = isStalled;
    $[25] = searchQuery.error;
    $[26] = searchQuery.isFetching;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
}